import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePTAEManagement } from '../hook/usePTAEManagement.mjs';
import { AuthContext } from '../../authProvider.mjs';
import ptaeService from '../service/ptaeService.mjs';
import { regionLabel } from './PTAERegions.mjs';
import { downloadPtaeTemplate } from '../util/ptaeTemplate.mjs';
import PageShell from '../../components/PageShell.mjs';


/** What a row's outstanding request is, said plainly on the pill. */
const PENDING_LABEL = {
  edit: 'Edit',
  suspend: 'Suspension',
  reactivate: 'Reactivation',
};

/**
 * A row with anything outstanding takes no further requests until it is decided.
 * Covers a pending addition too - a code nobody has approved yet cannot
 * meaningfully be suspended or renamed.
 */
const locked = (ptae) => !!ptae.pendingAction || ptae.approvalStatus === 'pending';

/**
 * The four parts of a PTAE code, in order.
 *
 * Maxima are deliberately loose - wide enough for anything Stanford has issued
 * (the longest real project is 7 characters, the longest award 5) and far
 * inside the model's STRING(64) - so they stop a paste accident or a runaway
 * keypress without rejecting a legitimate new code shape.
 */
const PTAE_PARTS = [
  { key: 'project', label: 'Project', placeholder: '1160682', max: 20, width: 'flex-[7] min-w-0 sm:flex-none sm:w-28' },
  { key: 'task',    label: 'Task',    placeholder: '002',     max: 12, width: 'flex-[4] min-w-0 sm:flex-none sm:w-20' },
  { key: 'award',   label: 'Award',   placeholder: 'AABHK',   max: 16, width: 'flex-[5] min-w-0 sm:flex-none sm:w-24' },
  { key: 'expense', label: 'Expense', placeholder: '54830',   max: 16, width: 'flex-[5] min-w-0 sm:flex-none sm:w-24' },
];

const PTAEManagement = () => {
  const {
    ptaes,
    loading,
    error,
    saving,
    loadPTAEs,
    addPTAE,
    updatePTAE,
    suspendPTAE,
    reactivatePTAE,
    setError: _setError
  } = usePTAEManagement();

  const [selectedColumnA, setSelectedColumnA] = useState('');
  const [selectedFund, setSelectedFund] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // A PTAE code is four parts - project-task.award.expense - and typing it as
  // one string was the only way to add one by hand. Nothing validated it: the
  // client checked `required` and the server checked "not empty", so a typo, a
  // missing separator or the wrong separator all saved happily and the code then
  // failed to parse, which drops it out of the claim-coding cascade entirely
  // (getPTAEOptions files unparseable rows under `unstructured`). Four fields
  // make the shape impossible to get wrong, and the server assembles nothing -
  // it receives the same joined string it always did.
  const [newPtae, setNewPtae] = useState({
    project: '', task: '', award: '', expense: '', description: '', reason: '',
  });
  const [editingPtae, setEditingPtae] = useState(null);
  // Edits, suspensions and reactivations all need a reason before they are sent,
  // so one prompt serves all three rather than three near-identical dialogs.
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [addNotice, setAddNotice] = useState(null);
  const [filteredPtaes, setFilteredPtaes] = useState([]);

  // The region comes from the URL - this page is always reached from the region
  // cards, so which chart of accounts is being edited is never ambiguous.
  // A regional admin is additionally pinned by the backend regardless of URL.
  const { region: regionParam } = useParams();
  const navigate = useNavigate();
  const { user, isAuditor } = useContext(AuthContext);
  const isGlobalRole = isAuditor
    || ['auditor', 'global_director', 'finance_director'].includes(user?.role);
  // Only the auditor asks for changes; the finance director views every region
  // here and decides requests in PTAE Approvals (the server enforces the same).
  const canChange = isAuditor;
  const region = regionParam || (isGlobalRole ? '' : (user?.region || ''));
  const [regionInfo, setRegionInfo] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginatedPtaes, setPaginatedPtaes] = useState([]);

  useEffect(() => {
    loadPTAEs('', '', region);
  }, [loadPTAEs, region]);

  // Region coverage, so the page can say whether a region has its own accounts
  // or is borrowing the fallback region's.
  const refreshRegions = useCallback(() => {
    ptaeService.getRegions()
      .then(setRegionInfo)
      .catch(() => { /* non-critical: the table still works without the summary */ });
  }, []);

  useEffect(() => { refreshRegions(); }, [refreshRegions]);

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await ptaeService.importPTAEs(importFile, region);
      setImportResult({ ok: true, ...result });
      setImportFile(null);
      await loadPTAEs('', '', region);
      refreshRegions();
    } catch (err) {
      setImportResult({ ok: false, message: err.message });
    } finally {
      setImporting(false);
    }
  };

  // Distinct funding programmes present, for the filter dropdown.
  const uniqueFunds = [...new Set(ptaes.map(p => p.fundName).filter(Boolean))].sort();

  const ownCount = regionInfo?.regions?.find(r => r.region === region)?.count ?? null;
  const isBorrowing = !!region
    && region !== regionInfo?.fallbackRegion
    && ownCount === null;

  useEffect(() => {
    const q = searchTerm.trim().toLowerCase();
    // Search every part of the chart string and the funding programme, so a code
    // can be found by project, task, award, expense code, its description OR the
    // programme it is funded from.
    const matches = (ptae) => {
      if (!q) return true;
      return [
        ptae.id, ptae.description, ptae.fundName,
        ptae.project, ptae.task, ptae.award, ptae.expense,
      ].some(v => String(v ?? '').toLowerCase().includes(q));
    };

    const filtered = ptaes.filter(ptae =>
      (selectedColumnA === '' || String(ptae.id).startsWith(selectedColumnA)) &&
      (selectedFund === '' || (ptae.fundName || '') === selectedFund) &&
      matches(ptae)
    );
    setFilteredPtaes(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [ptaes, selectedColumnA, selectedFund, searchTerm]);

  // Pagination logic
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedPtaes(filteredPtaes.slice(startIndex, endIndex));
  }, [filteredPtaes, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredPtaes.length / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredPtaes.length);

  const uniqueColumnAs = [...new Set(ptaes.map(ptae => ptae.id.split('-').slice(0, -1).join('-')))];

  /**
   * The four parts, joined the way the parser expects.
   *
   * `project-task.award.expense` - a hyphen after the project and dots after
   * that. This is the STORED form (ptaeImport.parsePtaeName drops the trailing
   * .0000.000 filler and the description suffix that appear in import files).
   */
  const composedPtaeCode = [newPtae.project, newPtae.task, newPtae.award, newPtae.expense]
    .map(part => part.trim())
    .every(Boolean)
    ? `${newPtae.project.trim()}-${newPtae.task.trim()}.${newPtae.award.trim()}.${newPtae.expense.trim()}`
    : '';

  const handleAddPtae = async (e) => {
    e.preventDefault();
    if (!composedPtaeCode) {
      _setError('Fill in all four parts of the code.');
      return;
    }
    if (!newPtae.reason.trim()) {
      _setError('Give a reason for adding this PTAE.');
      return;
    }
    setAddNotice(null);
    try {
      const added = await addPTAE(composedPtaeCode, newPtae.description, newPtae.reason.trim(), region);
      setNewPtae({ project: '', task: '', award: '', expense: '', description: '', reason: '' });
      // The new row lands at the end of the list, often pages away, so say here
      // that the request went to the finance director.
      setAddNotice(`${composedPtaeCode}: ${added?.message
        || 'Sent to the finance director for approval. It cannot be used on claims until they approve it.'}`);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleUpdatePtae = (e) => {
    e.preventDefault();
    if (!editingPtae) return;
    const newCode = (editingPtae.id || '').trim();
    if (!newCode) {
      _setError('PTAE code cannot be empty');
      return;
    }
    setReasonPrompt({
      kind: 'edit',
      code: editingPtae.originalId,
      title: `Edit ${editingPtae.originalId}`,
      // The consequence of a rename belongs here rather than in a separate
      // confirm, so the person writing the justification is looking at what
      // they are justifying.
      note: newCode !== editingPtae.originalId
        ? `This renames the code to "${newCode}". On approval, every claim, split `
          + `and monthly report line using the old code is updated to the new one.`
        : 'The current description stays in use until the finance director approves.',
      submit: (reason) => updatePTAE(editingPtae.originalId, {
        ptaeId: newCode,
        description: editingPtae.description,
        reason,
        ...(region ? { region } : {}),
      }),
      onDone: () => setEditingPtae(null),
    });
  };

  const handleSuspendPtae = (id) => {
    setReasonPrompt({
      kind: 'suspend',
      code: id,
      title: `Suspend ${id}`,
      note: 'The code stays selectable until the finance director approves. '
        + 'Existing claims that already use it are unaffected either way.',
      submit: (reason) => suspendPTAE(id, reason, region),
    });
  };

  const handleReactivatePtae = (id) => {
    setReasonPrompt({
      kind: 'reactivate',
      code: id,
      title: `Reactivate ${id}`,
      note: 'The code stays suspended until the finance director approves.',
      submit: (reason) => reactivatePTAE(id, reason, region),
    });
  };

  const submitReason = async (reason) => {
    if (!reasonPrompt) return;
    try {
      await reasonPrompt.submit(reason);
      reasonPrompt.onDone?.();
      setReasonPrompt(null);
    } catch (err) {
      // The hook surfaces the message; keep the prompt open so the text typed is
      // not lost when e.g. the new code clashes and has to be changed.
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (loading) {
    return (
      <PageShell title="PTAE Management">
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading PTAE data...</p>
        </div>
      </PageShell>
    );
  }

  return (
    <>
    <PageShell title="PTAE Management">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}


        {/* Region + bulk upload. Each region has its own chart of accounts; a
            region with none of its own borrows East Africa's until it uploads. */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Region
              </label>
              <div className="flex items-center gap-2">
                <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800">
                  {regionLabel(region)}
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/ptae-management')}
                  className="text-sm text-stanford-red hover:underline"
                >
                  Change region
                </button>
              </div>
            </div>

            {canChange ? (
            <form onSubmit={handleImport} className="flex flex-wrap gap-3 items-end">
              <div className="w-full sm:w-auto min-w-0">
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Upload Chart of Accounts (.csv / .xlsx)
                </label>
                <input
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="w-full sm:w-auto max-w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm file:mr-3 file:px-3 file:py-1 file:border-0 file:rounded file:bg-stanford-red file:text-white"
                />
              </div>
              <button
                type="submit"
                disabled={!importFile || importing || (isGlobalRole && !region)}
                title={isGlobalRole && !region ? 'Choose which region these PTAEs belong to' : ''}
                className="px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 text-sm font-medium"
              >
                {importing ? 'Importing…' : 'Import PTAEs'}
              </button>
              {/* The chart string has an exact shape and an unrecognised row is
                  skipped rather than guessed at, so an example of the expected
                  format belongs next to the upload, not in a separate doc. */}
              <button
                type="button"
                onClick={() => downloadPtaeTemplate(regionLabel(region))}
                title="Example workbook showing the columns and the chart-string format"
                className="px-4 py-2 border border-stanford-red text-stanford-red rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
              >
                ↓ Download template
              </button>
            </form>
            ) : (
              <p className="text-sm text-gray-600">
                View only. The auditor asks for changes, and you approve them in{' '}
                <button type="button" onClick={() => navigate('/ptae-approvals')} className="text-stanford-red hover:underline">
                  PTAE Approvals
                </button>.
              </p>
            )}
          </div>

          {isBorrowing && (
            <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {regionLabel(region)} has no chart of accounts of its own yet, so it is using{' '}
              {regionLabel(regionInfo?.fallbackRegion)}'s.
              {canChange && <>{' '}Uploading a file here switches {regionLabel(region)} to its own codes.</>}
            </p>
          )}

          {importResult && (
            <p className={`mt-3 text-sm rounded-lg px-3 py-2 border ${
              importResult.ok
                ? 'text-emerald-800 bg-emerald-50 border-emerald-200'
                : 'text-red-800 bg-red-50 border-red-200'
            }`}>
              {importResult.ok
                ? `${importResult.message}. ${importResult.added} added, ${importResult.updated} updated, ` +
                  `${importResult.suspended} marked inactive` +
                  (importResult.skippedCount ? `, ${importResult.skippedCount} skipped` : '') + '.'
                : importResult.message}
            </p>
          )}
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6 flex flex-wrap gap-4 items-center">
          <select
            value={selectedColumnA}
            onChange={(e) => setSelectedColumnA(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
          >
            <option value="">All PTAs</option>
            {uniqueColumnAs.map(columnA => (
              <option key={columnA} value={columnA}>{columnA}</option>
            ))}
          </select>
          {uniqueFunds.length > 0 && (
            <select
              value={selectedFund}
              onChange={(e) => setSelectedFund(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent max-w-[320px]"
            >
              <option value="">All funds</option>
              {uniqueFunds.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            placeholder="Search PTAEs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent w-full sm:w-auto sm:flex-1 sm:min-w-[200px]"
          />
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        {/* Add New PTAE Section */}
        {canChange && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-1">Add New PTAE</h2>
          <p className="text-sm text-gray-500 mb-4">
            Sent to the finance director for approval. The code cannot be used on a
            claim until they approve it.
          </p>
          {addNotice && (
            <p role="status" className="mb-4 px-3 py-2 rounded-lg text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 break-words">
              {addNotice}
            </p>
          )}
          <form onSubmit={handleAddPtae}>
            {/* Top-aligned: the code column carries part labels and a preview
                under its boxes, so bottom-aligning dropped Description below them. */}
            <div className="flex flex-wrap gap-4 items-start">
              <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[320px]">
                <span className="block text-sm font-medium text-gray-700 mb-1">PTAE Code</span>
                <div className="flex items-end gap-1">
                  {PTAE_PARTS.map(({ key, label, placeholder, max, width }, i) => (
                    <React.Fragment key={key}>
                      {i > 0 && (
                        <span className="pb-2 text-gray-400 select-none">{i === 1 ? '-' : '.'}</span>
                      )}
                      <div className={width}>
                        <input
                          id={`ptae-${key}`}
                          type="text"
                          aria-label={label}
                          placeholder={placeholder}
                          maxLength={max}
                          value={newPtae[key]}
                          // A separator typed INTO a part would produce a code
                          // the parser cannot read, so they are stripped rather
                          // than rejected - pasting a whole code into the first
                          // box is the obvious thing to try.
                          onChange={(e) => setNewPtae({
                            ...newPtae,
                            [key]: e.target.value.replace(/[\s.-]/g, ''),
                          })}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm leading-6 font-mono focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                          required
                        />
                        <span className="block text-[11px] text-gray-500 mt-0.5">{label}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {composedPtaeCode
                    ? <>Will be saved as <span className="font-mono text-gray-700">{composedPtaeCode}</span></>
                    : 'All four parts are needed.'}
                </p>
              </div>
              <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[200px]">
                <label htmlFor="ptae-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  id="ptae-description"
                  type="text"
                  placeholder="PTAE should match Sage description"
                  value={newPtae.description}
                  onChange={(e) => setNewPtae({...newPtae, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  required
                />
              </div>
            </div>
            {/* The reason is what the finance director judges the request on, so
                it is required rather than optional. */}
            <div className="mt-4 flex flex-wrap gap-4 items-end">
              <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[240px]">
                <label htmlFor="ptae-reason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for adding <span className="text-stanford-red">*</span>
                </label>
                <input
                  id="ptae-reason"
                  type="text"
                  placeholder="e.g. New award opened for the Nairobi cohort"
                  value={newPtae.reason}
                  onChange={(e) => setNewPtae({ ...newPtae, reason: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={saving || !newPtae.reason.trim()}
                title={!newPtae.reason.trim() ? 'A reason is required' : ''}
                className="px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span>+</span>
                    Request Approval
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        )}

        {/* PTAE List Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-800">PTAE List</h2>
            <span className="text-sm text-gray-500">
              Showing {filteredPtaes.length === 0 ? 0 : startItem}-{endItem} of {filteredPtaes.length} PTAEs
            </span>
          </div>

          {filteredPtaes.length === 0 ? (
            <div className="p-8 text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">No PTAEs Found</h3>
              <p className="text-gray-500">
                {ptaes.length === 0
                  ? `No PTAEs have been created yet.${canChange ? ' Add your first PTAE above.' : ''}`
                  : "No PTAEs match your current search criteria."
                }
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full responsive-table">
                  <thead className="bg-stanford-red text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">PTAE Code</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                      {isGlobalRole && !region && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Region</th>
                      )}
                      <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                      {canChange && <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPtaes.map(ptae => (
                      <tr key={ptae.id} className={`border-b border-gray-200 hover:bg-gray-50 ${ptae.isSuspended ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-3" data-cell="title">
                          {editingPtae && editingPtae.originalId === ptae.id ? (
                            <input
                              type="text"
                              value={editingPtae.id}
                              onChange={(e) => setEditingPtae({ ...editingPtae, id: e.target.value })}
                              className="w-full md:min-w-[220px] px-3 py-1 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                              placeholder="Enter PTAE code"
                            />
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {/* The funding programme sits in brackets above the
                                  code, so a reader can see what pot the money
                                  comes from without decoding the chart string. */}
                              {ptae.fundName && (
                                <span className="text-xs text-gray-500 leading-tight">
                                  ({ptae.fundName})
                                </span>
                              )}
                              <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-stanford-red w-fit">
                                {ptae.id}
                              </code>
                              {ptae.project && (
                                <span className="text-[10px] text-gray-400 leading-tight">
                                  project {ptae.project} · task {ptae.task} · award {ptae.award} · expense {ptae.expense}
                                </span>
                              )}
                            </div>
                          )}
                          {ptae.isSuspended && (
                            <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Suspended</span>
                          )}
                          {/* A pending request is stated as a request, because
                              nothing about the code has actually changed yet. */}
                          {ptae.approvalStatus === 'pending' ? (
                            <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Awaiting approval - not usable yet
                            </span>
                          ) : ptae.approvalStatus === 'rejected' ? (
                            <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Rejected
                            </span>
                          ) : ptae.pendingAction ? (
                            <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {PENDING_LABEL[ptae.pendingAction] || 'Change'} awaiting approval
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-700" data-label="Description">
                          {editingPtae && editingPtae.originalId === ptae.id ? (
                            <input
                              type="text"
                              value={editingPtae.description}
                              onChange={(e) => setEditingPtae({...editingPtae, description: e.target.value})}
                              className="w-full px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                              placeholder="PTAE should match Sage description"
                            />
                          ) : (
                            <div>
                              {ptae.description}
                              {!ptae.description && <em className="text-gray-400">No description provided</em>}
                            </div>
                          )}
                        </td>
                        {isGlobalRole && !region && (
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap" data-label="Region">
                            {regionLabel(ptae.region)}
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm text-gray-500" data-label="Created">
                          {ptae.createdAt ? new Date(ptae.createdAt).toLocaleDateString() : '-'}
                        </td>
                        {canChange && (
                        <td className="px-4 py-3" data-cell="action">
                          <div className="flex gap-2">
                            {editingPtae && editingPtae.originalId === ptae.id ? (
                              <>
                                <button
                                  onClick={handleUpdatePtae}
                                  disabled={saving}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditingPtae(null)}
                                  className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-100 transition-colors"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : locked(ptae) ? (
                              // One outstanding request per code: a second would
                              // race the first and the approver could not tell
                              // which one they were deciding.
                              <span className="text-xs text-gray-500 italic">
                                Waiting on the finance director
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={() => setEditingPtae({ ...ptae, originalId: ptae.id })}
                                  className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-100 transition-colors"
                                >
                                  Edit
                                </button>
                                {ptae.isSuspended ? (
                                  <button
                                    onClick={() => handleReactivatePtae(ptae.id)}
                                    disabled={saving}
                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                                  >
                                    Reactivate
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleSuspendPtae(ptae.id)}
                                    disabled={saving}
                                    className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 transition-colors disabled:opacity-50"
                                  >
                                    Suspend
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4 bg-gray-50">
                  <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &lsaquo; Previous
                    </button>

                    <div className="flex gap-1">
                      {getPageNumbers().map((page, index) => (
                        <button
                          key={index}
                          onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                          disabled={page === '...'}
                          className={`px-3 py-1 text-sm rounded transition-colors ${
                            page === currentPage
                              ? 'bg-stanford-red text-white'
                              : page === '...'
                                ? 'cursor-default text-gray-400'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next &rsaquo;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
    </PageShell>

      {/* A sibling of the PageShell, so the backdrop layers against the fixed
          navbar on its own z-index. */}
      {reasonPrompt && (
        <ReasonModal
          prompt={reasonPrompt}
          saving={saving}
          onCancel={() => setReasonPrompt(null)}
          onSubmit={submitReason}
        />
      )}
    </>
  );
};

/**
 * Collects the justification that goes to the finance director.
 *
 * Its own component so the reason text lives in local state: keeping it on the
 * page would re-render the whole PTAE table on every keystroke.
 */
const ReasonModal = ({ prompt, saving, onCancel, onSubmit }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{prompt.title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            This goes to the finance director for approval.
          </p>
        </div>

        <div className="px-6 py-5">
          {prompt.note && (
            <p className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {prompt.note}
            </p>
          )}
          <label htmlFor="change-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-stanford-red">*</span>
          </label>
          <textarea
            id="change-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            autoFocus
            placeholder="Why is this change needed?"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
          />
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reason.trim())}
            disabled={!reason.trim() || saving}
            title={!reason.trim() ? 'A reason is required' : ''}
            className="px-5 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-50 font-medium"
          >
            {saving ? 'Submitting…' : 'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PTAEManagement;
